import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './SampleReceivePage.css';
import './SpecimenWorklistRedesign.css';
import './EnterpriseSpecimenWorklist.css';
import sampleReceiveService from '../services/sampleReceiveService';
import { useAuth } from '../context/AuthContext';
import CommonPagination from '../components/common/CommonPagination';
import PatientOrder360Modal from '../components/common/PatientOrder360Modal';
import ThermalBarcodePrintModal from '../components/common/ThermalBarcodePrintModal';
import PAGINATION_CONFIG from '../config/paginationConfig';
import useSampleStatusCounts from '../hooks/useSampleStatusCounts';
import { buildSampleSearchParam } from '../utils/sampleSearch';
import { localISODate } from '../utils/worklistDates';
import { sampleRowStatusClass, STATUS_FILTER_LABELS, isOutsourcedSample } from '../utils/sampleStatus';
import {
  WorklistState,
  DateRangeFields,
  SampleSummaryCards,
  WorklistContextBar,
} from '../components/sampleWorklist/SampleWorklistParts';
import SampleWorklistGrid from '../components/sampleWorklist/SampleWorklistGrid';

export const SampleReceivePage = () => {
  const { user } = useAuth();
  // Filters State
  const [searchField, setSearchField] = useState('ALL');
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('S'); // Default to 'S' (Collected / Pending Accessioning)
  // Default the registration-date filter to today's local date (resolved at runtime)
  const [fromDate, setFromDate] = useState(() => localISODate());
  const [toDate, setToDate] = useState(() => localISODate());
  const [loading, setLoading] = useState(false);
  const [samples, setSamples] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGINATION_CONFIG.defaultPageSize);
  const [loadError, setLoadError] = useState(null);
  const [countsRefreshKey, setCountsRefreshKey] = useState(0);

  // Redesign Operational UX States
  const [viewMode] = useState('GRID'); // grouped view toggle removed from the UI; grid is the worklist
  const [quickFilter, setQuickFilter] = useState('ALL'); // 'ALL' | 'PENDING' | 'ELIGIBLE'
  const [sortBy] = useState('NEW_FIRST'); // 'NEW_FIRST' | 'OLD_FIRST' | 'NAME' | 'URGENT'
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [selectedPatientModal, setSelectedPatientModal] = useState(null);


  // Rejection Reasons Master List
  const [rejectionReasons, setRejectionReasons] = useState([]);

  // Multi-Selection State for Batch Receiving
  const [selectedTestIds, setSelectedTestIds] = useState(new Set());

  // Receive Confirmation Modal State
  const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
  const [receiveItems, setReceiveItems] = useState([]);
  const [receiveRemarks, setReceiveRemarks] = useState('');
  const [submittingReceive, setSubmittingReceive] = useState(false);

  // Rejection Modal State
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectItem, setRejectItem] = useState(null);
  const [selectedReasonId, setSelectedReasonId] = useState('');
  const [customReasonText, setCustomReasonText] = useState('');
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [submittingReject, setSubmittingReject] = useState(false);

  // Outsource Lab Assignment Modal State
  const [isOutsourceModalOpen, setIsOutsourceModalOpen] = useState(false);
  const [outsourceItem, setOutsourceItem] = useState(null);
  const [labOptions, setLabOptions] = useState([]);
  const [selectedLabId, setSelectedLabId] = useState('');
  const [loadingLabs, setLoadingLabs] = useState(false);
  const [submittingAssignLab, setSubmittingAssignLab] = useState(false);

  // Barcode Label Print Modal State
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [printItem, setPrintItem] = useState(null);

  const handleOpenPrintBarcode = (sample) => {
    setPrintItem(sample);
    setIsPrintModalOpen(true);
  };

  // Toast Notification
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4500);
  };


  // Fetch Rejection Reasons Master
  const fetchRejectionReasons = useCallback(async () => {
    try {
      const res = await sampleReceiveService.getRejectionReasons();
      if (res.success && res.data) {
        setRejectionReasons(res.data);
        if (res.data.length > 0) {
          setSelectedReasonId(res.data[0].rejectionId.toString());
        }
      }
    } catch (err) {
      console.error('Failed to fetch rejection reasons:', err);
    }
  }, []);

  // Reset page to 1 whenever filters change
  useEffect(() => {
    setPageNumber(1);
  }, [searchField, searchValue, statusFilter, fromDate, toDate]);

  // Filters shared by the worklist and the per-status counts (everything except status / paging)
  const filterParams = useMemo(() => {
    const params = buildSampleSearchParam(searchField, searchValue);
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return params;
  }, [searchField, searchValue, fromDate, toDate]);

  // Fetch Specimen List
  const fetchSamples = useCallback(async () => {
    setLoading(true);
    try {
      const res = await sampleReceiveService.searchSamples({
        ...filterParams,
        status: statusFilter,
        pageNumber,
        pageSize,
      });
      if (res.success && res.data) {
        setSamples(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
        setLoadError(null);
      } else {
        setSamples([]);
        setTotalCount(0);
        setLoadError(res.message || 'The server did not return the accession worklist.');
      }
    } catch (err) {
      console.error('Failed to load accession worklist:', {
        status: err.response?.status,
        body: err.response?.data,
        error: err,
      });
      const status = err.response?.status;
      setLoadError(
        err.response?.data?.message ||
          (status ? `Request failed (HTTP ${status}).` : 'The LIS server could not be reached. Check the network connection.')
      );
      setSamples([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filterParams, statusFilter, pageNumber, pageSize]);

  // Per-status totals, same endpoint and filters as the list
  const { counts, loading: countsLoading } = useSampleStatusCounts(
    sampleReceiveService.searchSamples,
    filterParams,
    countsRefreshKey
  );

  // Initial Load
  useEffect(() => {
    fetchRejectionReasons();
    fetchSamples();
  }, [fetchRejectionReasons, fetchSamples]);

  // Search Handler
  const handleSearch = (e) => {
    e.preventDefault();
    setSelectedTestIds(new Set());
    fetchSamples();
  };

  // Laser Barcode Scan Handler
  const handleBarcodeScan = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchField('BARCODE');
      setSelectedTestIds(new Set());
      fetchSamples();
    }
  };

  // Derive patient groups from current page samples (Order / Visit level grouping)
  const patientGroups = useMemo(() => {
    if (!samples || samples.length === 0) return [];
    const map = new Map();
    samples.forEach((sample) => {
      const key = sample.ledgerTransactionNo || sample.patientId || String(sample.ledgerTransactionId || sample.testId);
      if (!map.has(key)) {
        map.set(key, {
          ...sample,
          groupKey: key,
          patientName: sample.patientName || 'Unknown Patient',
          patientId: sample.patientId || '',
          ledgerTransactionNo: sample.ledgerTransactionNo || '',
          ageGender: sample.ageGender || '',
          mobile: sample.mobile || '',
          priority: sample.priority || 'Routine',
          isUrgent: Boolean(sample.isUrgent || sample.priority === 'Urgent'),
          centreName: sample.centreName || '',
          registrationDate: sample.registrationDate,
          samples: [],
        });
      }
      map.get(key).samples.push(sample);
    });

    return Array.from(map.values()).map((grp) => {
      const total = grp.samples.length;
      const awaiting = grp.samples.filter((s) => s.isSampleCollected === 'S').length;
      const received = grp.samples.filter((s) => s.isSampleCollected === 'Y').length;
      const rejected = grp.samples.filter((s) => s.isSampleCollected === 'R').length;
      const notCollected = grp.samples.filter((s) => s.isSampleCollected === 'N' || !s.isSampleCollected).length;
      const eligible = grp.samples.filter((s) => s.isSampleCollected === 'S' && !isOutsourcedSample(s));

      let statusCode = 'pending';
      let statusLabel = '⏳ Awaiting Receive';

      if (received === total && total > 0) {
        statusCode = 'received';
        statusLabel = '🔬 Fully Received in Lab';
      } else if (received > 0 && awaiting > 0) {
        statusCode = 'partial-received';
        statusLabel = `🔬 Partially Received (${received}/${total})`;
      } else if (awaiting === total && total > 0) {
        statusCode = 'collected';
        statusLabel = '🧪 Awaiting Receive (Collected)';
      } else if (rejected === total && total > 0) {
        statusCode = 'rejected';
        statusLabel = '⛔ All Rejected';
      }

      return {
        ...grp,
        pendingCount: notCollected,
        collectedCount: awaiting,
        awaitingReceiveCount: awaiting,
        receivedCount: received,
        rejectedCount: rejected,
        notCollectedCount: notCollected,
        eligibleSamples: eligible,
        groupStatus: { code: statusCode, label: statusLabel },
      };
    });
  }, [samples]);

  // Count of eligible collected items currently selected for receiving
  const selectedEligibleCount = useMemo(() => {
    return samples.filter((s) => selectedTestIds.has(s.testId) && s.isSampleCollected === 'S' && !isOutsourcedSample(s)).length;
  }, [samples, selectedTestIds]);

  // Container Cap Color & Styling Resolver
  const getContainerInfo = (containerName, containerColor) => {
    const cName = (containerName || '').toLowerCase();
    if (cName.includes('yellow') || cName.includes('sst') || cName.includes('gel')) {
      return { dotColor: '#eab308', bg: '#fefce8', border: '#fde047', label: containerName || 'Yellow Top (SST Gel Separator)' };
    }
    if (cName.includes('lavender') || cName.includes('purple') || cName.includes('edta')) {
      return { dotColor: '#a855f7', bg: '#faf5ff', border: '#e9d5ff', label: containerName || 'Lavender Top (K2/K3 EDTA)' };
    }
    if (cName.includes('red') || cName.includes('plain')) {
      return { dotColor: '#ef4444', bg: '#fef2f2', border: '#fecaca', label: containerName || 'Red Top (Plain Tube)' };
    }
    if (cName.includes('blue') || cName.includes('citrate')) {
      return { dotColor: '#0ea5e9', bg: '#f0f9ff', border: '#bae6fd', label: containerName || 'Light Blue Top (Sodium Citrate)' };
    }
    if (cName.includes('green') || cName.includes('heparin')) {
      return { dotColor: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0', label: containerName || 'Green Top (Lithium Heparin)' };
    }
    if (cName.includes('gray') || cName.includes('grey') || cName.includes('fluoride')) {
      return { dotColor: '#64748b', bg: '#f8fafc', border: '#cbd5e1', label: containerName || 'Gray Top (Sodium Fluoride)' };
    }
    if (cName.includes('cup') || cName.includes('urine') || cName.includes('sterile')) {
      return { dotColor: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', label: containerName || 'Sterile Universal Cup' };
    }
    const color = containerColor || '#6366f1';
    return { dotColor: color, bg: `${color}15`, border: `${color}40`, label: containerName || 'Standard Vacutainer' };
  };

  // Sorted Patient Groups based on user preference
  const sortedPatientGroups = useMemo(() => {
    let list = [...patientGroups];
    if (sortBy === 'NEW_FIRST') {
      list.sort((a, b) => new Date(b.registrationDate || 0) - new Date(a.registrationDate || 0));
    } else if (sortBy === 'OLD_FIRST') {
      list.sort((a, b) => new Date(a.registrationDate || 0) - new Date(b.registrationDate || 0));
    } else if (sortBy === 'NAME') {
      list.sort((a, b) => (a.patientName || '').localeCompare(b.patientName || ''));
    } else if (sortBy === 'URGENT') {
      list.sort((a, b) => (b.isUrgent ? 1 : 0) - (a.isUrgent ? 1 : 0));
    }
    return list;
  }, [patientGroups, sortBy]);

  // Filter samples inside a patient group according to quickFilter pill
  const getVisibleSamplesForGroup = (group) => {
    if (quickFilter === 'PENDING') {
      return group.samples.filter((s) => s.isSampleCollected === 'S'); // Awaiting Receive
    }
    if (quickFilter === 'ELIGIBLE') {
      return group.eligibleSamples;
    }
    return group.samples;
  };

  // Toggle patient card expansion/collapse
  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };





  // Global select/deselect all eligible visible samples (Collected = 'S' and NOT outsourced)
  const handleSelectAllVisible = () => {
    const eligible = samples.filter((s) => s.isSampleCollected === 'S' && !isOutsourcedSample(s));
    if (selectedTestIds.size === eligible.length && eligible.length > 0) {
      setSelectedTestIds(new Set());
    } else {
      setSelectedTestIds(new Set(eligible.map((s) => s.testId)));
      showToast(`${eligible.length} awaiting receive specimen(s) selected.`, 'info');
    }
  };

  // Selection Toggle
  const handleToggleSelect = (item, isCollected) => {
    const sample = typeof item === 'object' ? item : samples.find((x) => x.testId === item);
    if (!sample) return;
    if (sample.isSampleCollected !== 'S') return;
    if (isOutsourcedSample(sample)) {
      showToast('Outsourced specimens cannot be received in-house. Route to partner lab.', 'warning');
      return;
    }
    const testId = sample.testId;
    setSelectedTestIds((prev) => {
      const next = new Set(prev);
      if (next.has(testId)) {
        next.delete(testId);
      } else {
        next.add(testId);
      }
      return next;
    });
  };

  // Toggle selection for all eligible specimens of a patient group
  const handleTogglePatientGroup = (group) => {
    if (!group.eligibleSamples || group.eligibleSamples.length === 0) return;
    const allSelected = group.eligibleSamples.every((s) => selectedTestIds.has(s.testId));
    setSelectedTestIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        group.eligibleSamples.forEach((s) => next.delete(s.testId));
      } else {
        group.eligibleSamples.forEach((s) => next.add(s.testId));
      }
      return next;
    });
  };

  // Select All Pending in current list
  // Clear all selections
  const handleClearSelection = () => {
    setSelectedTestIds(new Set());
  };

  // Open Receive Modal for Single Item
  const handleOpenSingleReceive = (sample) => {
    if (sample.isSampleCollected === 'N') {
      showToast('Specimen has not been collected yet. Please collect first.', 'warning');
      return;
    }
    if (isOutsourcedSample(sample)) {
      showToast('Outsourced specimen cannot be received in-house. Route to partner lab.', 'warning');
      return;
    }
    if (sample.isSampleCollected === 'Y') {
      showToast('Specimen is already received in laboratory.', 'info');
      return;
    }
    if (sample.isSampleCollected === 'R') {
      showToast('Specimen was rejected.', 'error');
      return;
    }

    setReceiveItems([
      {
        testId: sample.testId,
        itemName: sample.itemName,
        itemCode: sample.itemCode,
        patientName: sample.patientName || sample.pName || 'Patient',
        patientId: sample.patientId,
        ledgerTransactionNo: sample.ledgerTransactionNo,
        departmentName: sample.departmentName,
        sampleTypeName: sample.sampleTypeName,
        containerName: sample.containerName,
        containerColor: sample.containerColor,
        barcodeNo: sample.barcodeNo || '',
        remarks: '',
        isOutsource: Boolean(sample.isOutsource || sample.processingMode === 'OUTSOURCE' || sample.referenceLabName),
        referenceLabName: sample.referenceLabName,
      },
    ]);
    setReceiveRemarks('');
    setIsReceiveModalOpen(true);
  };

  // Open Receive Confirmation Modal for all eligible specimens of a patient group
  const handleOpenPatientBulkReceive = (group) => {
    if (!group.eligibleSamples || group.eligibleSamples.length === 0) {
      showToast('No collected specimens eligible for receiving for this patient.', 'warning');
      return;
    }
    const items = group.eligibleSamples.map((s) => ({
      testId: s.testId,
      itemName: s.itemName,
      itemCode: s.itemCode,
      patientName: s.patientName || group.patientName,
      patientId: s.patientId || group.patientId,
      ledgerTransactionNo: s.ledgerTransactionNo || group.ledgerTransactionNo,
      departmentName: s.departmentName,
      sampleTypeName: s.sampleTypeName,
      containerName: s.containerName,
      containerColor: s.containerColor,
      barcodeNo: s.barcodeNo || '',
      remarks: '',
      isOutsource: Boolean(s.isOutsource || s.processingMode === 'OUTSOURCE' || s.referenceLabName),
      referenceLabName: s.referenceLabName,
    }));
    setReceiveItems(items);
    setReceiveRemarks('');
    setIsReceiveModalOpen(true);
  };

  // Open Receive Modal for Batch Selected Items
  const handleOpenBatchReceive = () => {
    if (selectedTestIds.size === 0) return;
    const selected = samples.filter(
      (s) => selectedTestIds.has(s.testId) && s.isSampleCollected === 'S' && !isOutsourcedSample(s)
    );
    if (selected.length === 0) {
      showToast('None of the selected specimens are currently eligible for receiving.', 'warning');
      return;
    }
    const items = selected.map((s) => ({
      testId: s.testId,
      itemName: s.itemName,
      itemCode: s.itemCode,
      patientName: s.patientName || s.pName || 'Patient',
      patientId: s.patientId,
      ledgerTransactionNo: s.ledgerTransactionNo,
      departmentName: s.departmentName,
      sampleTypeName: s.sampleTypeName,
      containerName: s.containerName,
      containerColor: s.containerColor,
      barcodeNo: s.barcodeNo || '',
      remarks: '',
      isOutsource: Boolean(s.isOutsource || s.processingMode === 'OUTSOURCE' || s.referenceLabName),
      referenceLabName: s.referenceLabName,
    }));
    setReceiveItems(items);
    setReceiveRemarks('');
    setIsReceiveModalOpen(true);
  };

  // Remarks change for individual specimen inside receive modal
  const handleReceiveModalRemarksChange = (index, value) => {
    setReceiveItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], remarks: value };
      return next;
    });
  };

  // Confirm Receive Action with atomic/partial failure feedback
  const handleConfirmReceive = async () => {
    setSubmittingReceive(true);
    try {
      const payload = {
        items: receiveItems.map((item) => ({
          testId: item.testId,
          remarks: item.remarks?.trim() || receiveRemarks.trim() || undefined,
        })),
      };

      const res = await sampleReceiveService.receiveSamples(payload);
      if (res.success) {
        const processedIds = res.data?.processedTestIds || receiveItems.map((m) => m.testId);
        const failedCount = res.data?.failedCount || 0;
        const msg = res.message || `Successfully received ${processedIds.length} specimen(s).`;

        showToast(msg, failedCount > 0 ? 'warning' : 'success');
        setIsReceiveModalOpen(false);

        // Deselect processed test IDs
        setSelectedTestIds((prev) => {
          const next = new Set(prev);
          processedIds.forEach((id) => next.delete(id));
          return next;
        });

        fetchSamples();
        setCountsRefreshKey((k) => k + 1);
      } else {
        showToast(res.message || 'Receive failed.', 'error');
      }
    } catch (err) {
      console.error('Receive error:', err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        'Failed to receive specimen.';
      showToast(errMsg, 'error');
    } finally {
      setSubmittingReceive(false);
    }
  };

  // Open Rejection Modal
  const handleOpenReject = (sample) => {
    if (sample.isSampleCollected === 'N') {
      showToast('Cannot reject specimen before collection.', 'warning');
      return;
    }
    if (sample.isSampleCollected === 'R') {
      showToast('Specimen is already marked as rejected.', 'info');
      return;
    }

    setRejectItem(sample);
    if (rejectionReasons.length > 0) {
      setSelectedReasonId(rejectionReasons[0].rejectionId.toString());
    }
    setCustomReasonText('');
    setRejectRemarks('');
    setIsRejectModalOpen(true);
  };

  // Confirm Rejection Action
  const handleConfirmReject = async () => {
    if (!rejectItem) return;

    let reasonText = '';
    const selectedObj = rejectionReasons.find(
      (r) => r.rejectionId.toString() === selectedReasonId
    );
    if (selectedObj) {
      reasonText = selectedObj.rejectionReason;
    } else if (customReasonText.trim()) {
      reasonText = customReasonText.trim();
    } else {
      showToast('Please select or specify a rejection reason.', 'warning');
      return;
    }

    setSubmittingReject(true);
    try {
      const payload = {
        items: [
          {
            testId: rejectItem.testId,
            rejectionReasonId: selectedObj ? selectedObj.rejectionId : undefined,
            rejectionReason: reasonText,
            remarks: rejectRemarks.trim() || undefined,
          },
        ],
      };

      const res = await sampleReceiveService.rejectSamples(payload);
      if (res.success) {
        showToast(`Specimen #${rejectItem.testId} rejected. Reason: ${reasonText}`, 'success');
        setIsRejectModalOpen(false);
        fetchSamples();
        setCountsRefreshKey((k) => k + 1);
      } else {
        showToast(res.message || 'Rejection failed.', 'error');
      }
    } catch (err) {
      console.error('Reject error:', err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        'Failed to reject specimen.';
      showToast(errMsg, 'error');
    } finally {
      setSubmittingReject(false);
    }
  };

  // Open Outsource Lab Selection Modal
  const handleOpenOutsourceModal = async (sample) => {
    setOutsourceItem(sample);
    setIsOutsourceModalOpen(true);
    setLoadingLabs(true);
    setSelectedLabId('');
    try {
      const res = await sampleReceiveService.getLabsForTest(sample.testId);
      if (res.success && res.data) {
        setLabOptions(res.data);
        if (sample.referenceLabId) {
          setSelectedLabId(String(sample.referenceLabId));
        } else if (res.data.length > 0) {
          const firstLabId = res.data[0].referenceLabId ?? res.data[0].referenceLabID;
          if (firstLabId != null) {
            setSelectedLabId(String(firstLabId));
          }
        }
      } else {
        setLabOptions([]);
      }
    } catch (err) {
      console.error('Failed to load reference labs for test:', err);
      showToast('Failed to load mapped reference labs', 'error');
    } finally {
      setLoadingLabs(false);
    }
  };

  // Confirm Assign Outsource Lab
  const handleConfirmAssignOutsourceLab = async () => {
    if (!selectedLabId) {
      showToast('Please select a reference laboratory', 'warning');
      return;
    }
    setSubmittingAssignLab(true);
    try {
      const res = await sampleReceiveService.assignOutsourceLab({
        testId: outsourceItem.testId,
        referenceLabId: parseInt(selectedLabId, 10),
      });
      if (res.success) {
        showToast(res.message || 'Reference lab assigned successfully', 'success');
        setIsOutsourceModalOpen(false);
        fetchSamples();
      } else {
        showToast(res.message || 'Failed to assign lab', 'error');
      }
    } catch (err) {
      console.error('Failed to assign outsource lab:', err);
      showToast(err.response?.data?.message || 'Error assigning reference lab', 'error');
    } finally {
      setSubmittingAssignLab(false);
    }
  };

  // Patient visit & audit details (Log)
  const openPatientLog = (sample) => {
    const foundGroup = patientGroups.find((g) => g.patientId === sample.patientId);
    setSelectedPatientModal(foundGroup || { patientName: sample.patientName, samples: [sample], ...sample });
  };

  // Fixed action slots per row (Receive: S & !outsourced · Reject: S · Outsource: always · Log: always);
  // Label button is excluded as Barcode column already provides 🖨️ label print.
  // Unavailable slots are frozen (disabled), never removed, so buttons line up identically in every row.
  const getReceiveActions = (sample) => {
    const st = sample.isSampleCollected;
    const isOutsourced = isOutsourcedSample(sample);

    return [
      {
        key: 'receive', label: 'Receive', icon: 'receive', tone: 'receive',
        state: isOutsourced ? 'na' : st === 'S' ? 'ready' : st === 'Y' ? 'done' : 'na',
        title: isOutsourced
          ? 'Outsourced specimen cannot be received in-house (route/dispatch to partner lab)'
          : st === 'S'
          ? 'Receive & accession specimen into lab'
          : st === 'Y'
          ? 'Specimen already received (accessioned)'
          : st === 'N'
          ? 'Specimen not collected yet'
          : 'Rejected specimen cannot be received',
        onClick: isOutsourced ? undefined : () => handleOpenSingleReceive(sample),
      },
      {
        key: 'reject', label: 'Reject', icon: 'reject', tone: 'danger',
        state: st === 'S' ? 'ready' : st === 'R' ? 'done' : 'na',
        title: st === 'S' ? 'Reject specimen' : st === 'R' ? 'Specimen already rejected' : 'Only collected specimens can be rejected at receiving',
        onClick: () => handleOpenReject(sample),
      },
      {
        key: 'outsource', label: 'Outsource', icon: 'lab', tone: 'outsource', size: 'md', state: 'ready',
        title: sample.referenceLabName ? `Outsource partner: ${sample.referenceLabName}` : 'Assign outsource partner lab',
        onClick: () => handleOpenOutsourceModal(sample),
      },
      {
        key: 'log', label: 'Log', icon: 'log', tone: 'neutral', size: 'sm', state: 'ready',
        title: 'View patient visit & audit details',
        onClick: () => openPatientLog(sample),
      },
    ];
  };

  return (
    <div className="elw-root">
      {/* Toast Notification */}
      {toast && (
        <div className={`sw-toast sw-toast-${toast.type}`}>
          <span className="sw-toast-icon">
            {toast.type === 'success' ? '✓' : toast.type === 'warning' ? '⚠' : '✕'}
          </span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* ── 2. Status strip (segments = status filter), colour-coded ── */}
      <SampleSummaryCards
        cards={[
          { key: 'ALL', label: 'Total', tone: 'neutral', count: counts.ALL },
          { key: 'N', label: 'Pending', tone: 'pending', count: counts.N, hint: 'Registered, awaiting collection' },
          { key: 'S', label: 'Collected', tone: 'collected', count: counts.S, hint: 'Collected, awaiting receipt in lab' },
          { key: 'Y', label: 'Received', tone: 'received', count: counts.Y },
          { key: 'OUTSOURCE', label: 'Outsourced', tone: 'outsource', count: counts.OUTSOURCE },
          { key: 'R', label: 'Rejected', tone: 'rejected', count: counts.R },
        ]}
        active={statusFilter}
        loading={countsLoading}
        onSelect={(key) => {
          setStatusFilter(key);
          setSelectedTestIds(new Set());
        }}
      />
      <WorklistContextBar fromDate={fromDate} toDate={toDate} statusLabel={STATUS_FILTER_LABELS[statusFilter]} />

      {/* ── 3. Filter bar ── */}
      <form className="swl-filterbar" onSubmit={handleSearch} aria-label="Worklist filters">
        <DateRangeFields
          idPrefix="sr"
          fromDate={fromDate}
          toDate={toDate}
          onFromChange={setFromDate}
          onToChange={setToDate}
        />
        <div className="swl-filter-field swl-filter-search">
          <label htmlFor="sr-search">Search / scan patient, Lab No, UHID, barcode</label>
          <div className="swl-search-row">
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              title="Identifier to search"
              aria-label="Identifier to search"
            >
              <option value="ALL">All Identifiers</option>
              <option value="BARCODE">Barcode / SIN</option>
              <option value="UHID">UHID</option>
              <option value="LABNO">Lab No</option>
              <option value="PATIENT">Patient Name</option>
              <option value="SAMPLENO">Sample / Test No</option>
              <option value="INVESTIGATION">Test Name</option>
            </select>
            <input
              id="sr-search"
              type="text"
              placeholder="Scan tube barcode or type, then Enter"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={handleBarcodeScan}
              autoComplete="off"
              autoFocus
            />
          </div>
        </div>
        <div className="swl-filter-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            Search
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setSearchValue('');
              setSearchField('ALL');
              setFromDate('');
              setToDate('');
              setStatusFilter('S');
            }}
          >
            Reset
          </button>
        </div>
      </form>

      {/* ── 4. Compact Contextual Bulk Action Bar ── */}
      {selectedTestIds.size > 0 && (
        <section className="elw-bulk-bar">
          <div className="elw-bulk-left">
            <span className="elw-bulk-count">
              <strong>{selectedTestIds.size}</strong> specimen{selectedTestIds.size !== 1 ? 's' : ''} selected
            </span>
          </div>

          <div className="elw-bulk-actions">
            <button
              type="button"
              className="elw-btn-bulk-collect"
              onClick={handleOpenBatchReceive}
              disabled={selectedEligibleCount === 0}
              title={
                selectedEligibleCount === 0
                  ? 'No selected items are eligible for receiving'
                  : `Receive ${selectedEligibleCount} selected collected sample(s)`
              }
            >
              📥 Receive Selected ({selectedEligibleCount})
            </button>

            <button
              type="button"
              className="elw-btn-bulk-print"
              onClick={() => {
                const firstWithBarcode = samples.find((s) => selectedTestIds.has(s.testId) && s.barcodeNo);
                if (firstWithBarcode) {
                  showToast(`Barcode: ${firstWithBarcode.barcodeNo} (${firstWithBarcode.itemName})`, 'info');
                } else {
                  showToast('No barcodes allocated for selected samples', 'info');
                }
              }}
              title="Verify Barcode Labels for Selected"
            >
              🖨️ Verify Barcodes
            </button>

            <button
              type="button"
              className="elw-btn-bulk-clear"
              onClick={handleClearSelection}
              title="Clear current selection"
            >
              ✕ Clear Selection
            </button>
          </div>
        </section>
      )}

      {/* ── 5. Main Worklist Presentation Area (Enterprise Table-First Architecture) ── */}
      <main className="elw-main">
        {loading ? (
          <div className="elw-table-wrapper">
            <table className="elw-table">
              <thead>
                <tr>
                  <th className="elw-th-check"><span className="elw-th-shimmer" style={{ width: '18px', height: '18px' }} /></th>
                  <th className="elw-th-sno">#</th>
                  <th className="elw-th-test">Investigation / Test</th>
                  <th className="elw-th-dept">Department</th>
                  <th className="elw-th-specimen">Specimen & Container</th>
                  <th className="elw-th-barcode">Barcode / SIN</th>
                  <th className="elw-th-status">Status</th>
                  <th className="elw-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <tr key={n} className="elw-row-skeleton">
                    <td colSpan="8">
                      <div className="elw-skeleton-line" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : loadError ? (
          <WorklistState
            type="error"
            title="Unable to load sample worklist"
            message={loadError}
            actionLabel="Retry"
            onAction={() => {
              fetchSamples();
              setCountsRefreshKey((k) => k + 1);
            }}
          />
        ) : samples.length === 0 ? (
          <div className="elw-empty-card">
            <div className="elw-empty-icon">📥</div>
            <h3 className="elw-empty-title">No Specimen Samples Found</h3>
            <p className="elw-empty-desc">
              {statusFilter === 'S'
                ? 'All collected specimens have already been accessioned/received.'
                : 'No specimen records match the current filter criteria or search keyword.'}
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button
                type="button"
                className="elw-btn-p-collect"
                onClick={() => {
                  setStatusFilter('S');
                  setSearchValue('');
                  setQuickFilter('ALL');
                }}
              >
                Reset to Collected Samples
              </button>
              <button
                type="button"
                className="elw-btn-outline-sm"
                onClick={fetchSamples}
              >
                🔄 Refresh
              </button>
            </div>
          </div>
        ) : viewMode === 'GRID' ? (
          <SampleWorklistGrid
            samples={samples}
            selectedIds={selectedTestIds}
            isSelectable={(s) => s.isSampleCollected === 'S' && !isOutsourcedSample(s)}
            onToggleSelect={handleToggleSelect}
            allSelected={
              samples.some((s) => s.isSampleCollected === 'S' && !isOutsourcedSample(s)) &&
              selectedTestIds.size === samples.filter((s) => s.isSampleCollected === 'S' && !isOutsourcedSample(s)).length
            }
            onToggleAll={handleSelectAllVisible}
            selectAllTitle="Select all collected specimens awaiting receive"
            getContainerInfo={getContainerInfo}
            onPrintBarcode={handleOpenPrintBarcode}
            getActions={getReceiveActions}
            actionSlots={{ md: 3, sm: 1 }}
          />
        ) : (
          /* ── High-Density Single Worklist Table (Patient-Wise Embedded Groups) ── */
          <div className="elw-table-wrapper">
            <table className="elw-table">
              <thead>
                <tr>
                  <th className="elw-th-check">
                    <input
                      type="checkbox"
                      checked={
                        samples.filter((s) => s.isSampleCollected === 'S').length > 0 &&
                        selectedTestIds.size === samples.filter((s) => s.isSampleCollected === 'S').length
                      }
                      onChange={handleSelectAllVisible}
                      title="Select all collected specimens awaiting receive"
                      aria-label="Select all eligible specimens"
                    />
                  </th>
                  <th className="elw-th-sno">#</th>
                  <th className="elw-th-test">Investigation / Test</th>
                  <th className="elw-th-dept">Department</th>
                  <th className="elw-th-specimen">Specimen & Container</th>
                  <th className="elw-th-barcode">Barcode / SIN</th>
                  <th className="elw-th-status">Status</th>
                  <th className="elw-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedPatientGroups.map((group, groupIdx) => {
                  const isCollapsed = collapsedGroups.has(group.groupKey);
                  const visibleSamples = getVisibleSamplesForGroup(group);
                  const isGroupAllEligibleSelected =
                    group.eligibleSamples.length > 0 &&
                    group.eligibleSamples.every((s) => selectedTestIds.has(s.testId));

                  return (
                    <React.Fragment key={group.groupKey}>
                      {/* Compact Patient Identity Strip */}
                      <tr className="elw-patient-group-row">
                        <td colSpan="8" className="elw-patient-strip-td">
                          <div className={`elw-patient-strip ${group.priority === 'Urgent' || group.isUrgent ? 'is-urgent' : ''}`}>
                            <div className="elw-patient-strip-left">
                              <label className="elw-pcheck" title="Select all collected specimens for this patient">
                                <input
                                  type="checkbox"
                                  checked={isGroupAllEligibleSelected}
                                  disabled={group.eligibleSamples.length === 0}
                                  onChange={() => handleTogglePatientGroup(group)}
                                  aria-label={`Select all eligible for ${group.patientName}`}
                                />
                              </label>

                              <span className="elw-pindex font-mono">{groupIdx + 1}</span>

                              <span className="elw-pname" title={group.patientName}>{group.patientName}</span>

                              {(group.priority === 'Urgent' || group.isUrgent) && (
                                <span className="elw-purgent">🚨 URGENT</span>
                              )}

                              <span className="elw-pdemog">{group.ageGender || '—'}</span>

                              {group.mobile && (
                                <span className="elw-pchip" title="Patient Mobile">📞 {group.mobile}</span>
                              )}
                              <span className="elw-pchip font-mono" title="UHID / Patient ID">
                                <span className="elw-pchip-lbl">UHID:</span>{group.patientId}
                              </span>
                              <span className="elw-pchip font-mono" title="Lab Transaction No">
                                <span className="elw-pchip-lbl">LAB:</span>{group.ledgerTransactionNo}
                              </span>
                            </div>

                            <div className="elw-patient-strip-right">
                              <span className="elw-ptest-count">
                                {group.samples.length} Specimen{group.samples.length !== 1 ? 's' : ''}
                              </span>

                              <button
                                type="button"
                                className="elw-btn-preceive"
                                onClick={() => handleOpenPatientBulkReceive(group)}
                                disabled={group.eligibleSamples.length === 0}
                                title={
                                  group.eligibleSamples.length === 0
                                    ? 'No collected specimens eligible for receiving'
                                    : `Receive all ${group.eligibleSamples.length} collected specimens for ${group.patientName}`
                                }
                              >
                                📥 Receive All ({group.eligibleSamples.length})
                              </button>

                              <button
                                type="button"
                                className="elw-btn-plog"
                                onClick={() => setSelectedPatientModal(group)}
                                title="View Patient Visit & Complete Audit Details"
                              >
                                📜 360° Log
                              </button>

                              <button
                                type="button"
                                className="elw-btn-collapse"
                                aria-label={isCollapsed ? 'Expand patient tests' : 'Collapse patient tests'}
                                onClick={() => toggleGroupCollapse(group.groupKey)}
                                title={isCollapsed ? 'Expand tests' : 'Collapse tests'}
                              >
                                {isCollapsed ? '▼' : '▲'}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* Specimen Test Rows */}
                      {!isCollapsed &&
                        visibleSamples.map((sample, sIdx) => {
                          const isCollected = sample.isSampleCollected === 'S';
                          const isReceived = sample.isSampleCollected === 'Y';
                          const isRejected = sample.isSampleCollected === 'R';
                          const isOutsource = Boolean(
                            sample.isOutsource || sample.processingMode === 'OUTSOURCE' || sample.referenceLabName
                          );
                          const isSelected = selectedTestIds.has(sample.testId);
                          const tubeInfo = getContainerInfo(sample.containerName, sample.containerColor);

                          return (
                            <tr key={sample.testId} className={`elw-row ${sampleRowStatusClass(sample)} ${isSelected ? 'is-selected' : ''}`}>
                              {/* Selection */}
                              <td className="elw-td-check">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={!isCollected}
                                  onChange={() => handleToggleSelect(sample.testId, sample.isSampleCollected)}
                                  aria-label={`Select ${sample.itemName}`}
                                />
                              </td>

                              {/* S.No */}
                              <td className="elw-td-sno font-mono">{sIdx + 1}</td>

                              {/* Investigation / Test */}
                              <td className="elw-td-test">
                                <div className="elw-test-title-col">
                                  <span className="elw-test-name">{sample.itemName}</span>
                                  {isOutsource && (
                                    <span className="elw-outsource-tag">
                                      🌐 OUTSOURCE • {sample.referenceLabName || 'Reference Lab'}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Department */}
                              <td className="elw-td-dept">
                                <span className="elw-dept-badge">{sample.departmentName || 'General'}</span>
                              </td>

                              {/* Specimen & Container */}
                              <td className="elw-td-specimen">
                                <div className="elw-specimen-cell">
                                  <span
                                    className="elw-swatch-box"
                                    style={{
                                      backgroundColor: tubeInfo.dotColor,
                                      borderColor: tubeInfo.border,
                                    }}
                                    title={`${tubeInfo.label}${sample.containerName ? ` (${sample.containerName})` : ''}`}
                                    aria-label={tubeInfo.label}
                                  />
                                  <span className="elw-specimen-name">{sample.sampleTypeName || 'Serum'}</span>
                                </div>
                              </td>

                              {/* Barcode / SIN */}
                              <td className="elw-td-barcode">
                                <div className="elw-barcode-cell">
                                  {sample.barcodeNo ? (
                                    <>
                                      <span className="elw-barcode-pill" title={sample.barcodeNo}>
                                        🏷️ {sample.barcodeNo}
                                      </span>
                                      <button
                                        type="button"
                                        className="elw-btn-barcode-icon"
                                        title="Print Thermal Barcode Label"
                                        onClick={() => handleOpenPrintBarcode(sample)}
                                      >
                                        🖨️
                                      </button>
                                    </>
                                  ) : (
                                    <span className="elw-na-text">—</span>
                                  )}
                                </div>
                              </td>

                              {/* Status */}
                              <td className="elw-td-status">
                                <span
                                  className={`elw-status-pill status-${isReceived ? 'received' : isCollected ? 'collected' : isRejected ? 'rejected' : 'pending'}`}
                                >
                                  {isReceived
                                    ? 'RECEIVED'
                                    : isCollected
                                    ? 'COLLECTED'
                                    : isRejected
                                    ? 'REJECTED'
                                    : 'PENDING'}
                                </span>
                              </td>

                              {/* Actions */}
                              <td className="elw-td-actions">
                                <div className="elw-actions-flex">
                                  {isCollected && (
                                    <button
                                      type="button"
                                      className={`elw-btn-action-receive ${isOutsourcedSample(sample) ? 'is-disabled' : ''}`}
                                      disabled={isOutsourcedSample(sample)}
                                      onClick={isOutsourcedSample(sample) ? undefined : () => handleOpenSingleReceive(sample)}
                                      style={isOutsourcedSample(sample) ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                                      title={isOutsourcedSample(sample) ? 'Outsourced specimen cannot be received in-house' : 'Receive & Accession specimen into lab'}
                                    >
                                      📥 Receive
                                    </button>
                                  )}

                                  {isReceived && (
                                    <span className="recv-ready-tag font-mono">✓ Accessioned</span>
                                  )}

                                  {isCollected && !isRejected && (
                                    <button
                                      type="button"
                                      className="elw-btn-action-reject"
                                      onClick={() => handleOpenReject(sample)}
                                      title="Reject specimen"
                                    >
                                      ✕ Reject
                                    </button>
                                  )}

                                  <button
                                    type="button"
                                    className="elw-btn-action-outsource"
                                    title={sample.referenceLabName ? `Partner: ${sample.referenceLabName}` : 'Assign Outsource Partner Lab'}
                                    onClick={() => handleOpenOutsourceModal(sample)}
                                  >
                                    🌐 Outsource
                                  </button>

                                  <button
                                    type="button"
                                    className="elw-btn-action-log"
                                    onClick={() => setSelectedPatientModal(group)}
                                    title="View Patient Visit & Audit Details"
                                  >
                                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8" />
                                    </svg>
                                    Log
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Centralized Pagination Bar */}
        <div className="elw-pagination-bar">
          <CommonPagination
            page={pageNumber}
            pageSize={pageSize}
            totalRecords={totalCount}
            onPageChange={(p) => setPageNumber(p)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPageNumber(1);
            }}
            loading={loading}
          />
        </div>
      </main>

{/* Receive Confirmation Modal */}
      {isReceiveModalOpen && (
        <div className="recv-modal-overlay">
          <div className="recv-modal-card">
            <div className="recv-modal-header">
              <div>
                <h2 className="recv-modal-title">Specimen Accessioning Confirmation</h2>
                <p className="recv-modal-subtitle">
                  Verify specimen tubes, check barcodes, and accept into laboratory for testing.
                </p>
              </div>
              <button
                type="button"
                className="recv-modal-close"
                onClick={() => setIsReceiveModalOpen(false)}
                disabled={submittingReceive}
              >
                ✕
              </button>
            </div>

            <div className="recv-modal-body">
              {/* Receiving Technician Bar */}
              <div className="recv-receiver-bar">
                <div className="recv-rb-item">
                  <span className="recv-rb-label">Receiving Technician:</span>
                  <span className="recv-rb-value">
                    👤 {user?.employeeName || user?.username || 'Lab Staff'}
                  </span>
                </div>
                <div className="recv-rb-item">
                  <span className="recv-rb-label">Accession Timestamp:</span>
                  <span className="recv-rb-value">
                    🕒 {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} (Current Time)
                  </span>
                </div>
              </div>

              {/* List of Tubes / Tests to Accession */}
              <div className="recv-tubes-list">
                <h3 className="recv-tubes-title">Specimen Tubes to Receive ({receiveItems.length})</h3>
                {receiveItems.map((item, index) => {
                  const tubeInfo = getContainerInfo(item.containerName, item.containerColor);
                  return (
                    <div
                      key={item.testId}
                      className={`recv-tube-card ${item.isOutsource ? 'recv-tube-card-outsource' : ''}`}
                    >
                      <div className="recv-tube-info">
                        <div className="recv-tube-name">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <strong>{item.itemName}</strong>
                            {item.isOutsource && (
                              <span className="badge-outsource-recv" title={item.referenceLabName ? `Partner Lab: ${item.referenceLabName}` : 'Outsource Lab'}>
                                🌐 OUTSOURCE{item.referenceLabName ? ` • ${item.referenceLabName}` : ''}
                              </span>
                            )}
                          </div>
                          <span className="recv-tube-dept">({item.departmentName})</span>
                        </div>
                        <div className="recv-tube-demog">
                          <span>Patient: <strong>{item.patientName}</strong></span> • 
                          <span>LabNo: <strong>{item.ledgerTransactionNo}</strong></span> • 
                          <span>UHID: <strong>{item.patientId}</strong></span>
                        </div>
                        <div className="recv-tube-specimen-props">
                          <span className="recv-badge-sample">Specimen: {item.sampleTypeName}</span>
                          <span
                            className="recv-badge-container"
                            style={{
                              borderColor: tubeInfo.border,
                              color: tubeInfo.dotColor === '#eab308' ? '#854d0e' : tubeInfo.dotColor,
                              backgroundColor: tubeInfo.bg,
                            }}
                          >
                            ● {tubeInfo.label}
                          </span>
                          {item.barcodeNo && (
                            <span className="recv-badge-barcode font-mono">
                              🏷️ {item.barcodeNo}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="recv-tube-inputs">
                        <div className="recv-fg">
                          <label>Verified Barcode / SIN</label>
                          <input
                            type="text"
                            value={item.barcodeNo || 'No barcode assigned'}
                            disabled
                            className="recv-input font-mono"
                            style={{ backgroundColor: '#f8fafc', color: '#1e293b' }}
                          />
                        </div>
                        <div className="recv-fg">
                          <label>Accession Condition / Notes (Optional)</label>
                          <input
                            type="text"
                            value={item.remarks || ''}
                            placeholder="e.g. Specimen intact, adequate volume, rack A-1..."
                            onChange={(e) => handleReceiveModalRemarksChange(index, e.target.value)}
                            className="recv-input"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* General / Batch Accessioning Remarks */}
              <div className="recv-form-group">
                <label htmlFor="receiveRemarks">Batch Accessioning Notes / Lab Observations (Optional)</label>
                <textarea
                  id="receiveRemarks"
                  rows="2"
                  placeholder="Additional notes on specimen condition, laboratory intake notes, analyzer batch..."
                  value={receiveRemarks}
                  onChange={(e) => setReceiveRemarks(e.target.value)}
                  className="recv-input"
                ></textarea>
              </div>
            </div>

            <div className="recv-modal-footer">
              <button
                type="button"
                className="recv-btn recv-btn-secondary"
                onClick={() => setIsReceiveModalOpen(false)}
                disabled={submittingReceive}
              >
                Cancel
              </button>
              <button
                type="button"
                className="recv-btn recv-btn-primary recv-btn-receive"
                onClick={handleConfirmReceive}
                disabled={submittingReceive}
              >
                {submittingReceive
                  ? 'Accessioning...'
                  : receiveItems.length === 1
                  ? '✓ Confirm & Receive Specimen'
                  : `✓ Confirm & Receive All ${receiveItems.length} Specimens`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sample Rejection Modal */}
      {isRejectModalOpen && rejectItem && (
        <div className="recv-modal-overlay">
          <div className="recv-modal-card recv-modal-reject-card">
            <div className="recv-modal-header recv-modal-header-reject">
              <div>
                <h2 className="recv-modal-title">Reject Specimen Tube</h2>
                <p className="recv-modal-subtitle">
                  Rejecting a specimen halts result entry and requires phlebotomy recollection.
                </p>
              </div>
              <button
                type="button"
                className="recv-modal-close"
                onClick={() => setIsRejectModalOpen(false)}
                disabled={submittingReject}
              >
                ✕
              </button>
            </div>

            <div className="recv-modal-body">
              <div className="recv-reject-warning">
                <span className="recv-warning-icon">⚠️</span>
                <div>
                  <strong>Attention Laboratory Technician:</strong>
                  <p>
                    You are rejecting specimen for test <strong>{rejectItem.itemName}</strong> (Barcode: <code>{rejectItem.barcodeNo}</code>) for patient <strong>{rejectItem.patientName}</strong>.
                  </p>
                </div>
              </div>

              {/* Rejection Reason Selection */}
              <div className="recv-form-group">
                <label htmlFor="rejectionReason">Select Rejection Reason (Mandatory)</label>
                <select
                  id="rejectionReason"
                  value={selectedReasonId}
                  onChange={(e) => setSelectedReasonId(e.target.value)}
                  className="recv-select"
                >
                  {rejectionReasons.map((reason) => (
                    <option key={reason.rejectionId} value={reason.rejectionId}>
                      {reason.rejectionReason}
                    </option>
                  ))}
                  <option value="OTHER">Other / Custom Reason</option>
                </select>
              </div>

              {selectedReasonId === 'OTHER' && (
                <div className="recv-form-group">
                  <label htmlFor="customReason">Custom Rejection Reason</label>
                  <input
                    id="customReason"
                    type="text"
                    placeholder="Specify exact reason for specimen rejection..."
                    value={customReasonText}
                    onChange={(e) => setCustomReasonText(e.target.value)}
                    className="recv-input"
                  />
                </div>
              )}

              <div className="recv-form-group">
                <label htmlFor="rejectRemarks">Additional Rejection Remarks / Laboratory Note</label>
                <textarea
                  id="rejectRemarks"
                  rows="2"
                  placeholder="Provide clinical observations regarding sample condition (e.g. clot noted in EDTA tube)..."
                  value={rejectRemarks}
                  onChange={(e) => setRejectRemarks(e.target.value)}
                  className="recv-input"
                ></textarea>
              </div>
            </div>

            <div className="recv-modal-footer">
              <button
                type="button"
                className="recv-btn recv-btn-secondary"
                onClick={() => setIsRejectModalOpen(false)}
                disabled={submittingReject}
              >
                Cancel
              </button>
              <button
                type="button"
                className="recv-btn recv-btn-danger"
                onClick={handleConfirmReject}
                disabled={submittingReject}
              >
                {submittingReject ? 'Rejecting...' : '⛔ Confirm Specimen Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Outsource Lab Assignment Modal */}
      {isOutsourceModalOpen && outsourceItem && (
        <div className="recv-modal-overlay">
          <div className="recv-modal-card recv-modal-outsource-card">
            <div className="recv-modal-header recv-modal-header-outsource">
              <div>
                <h2 className="recv-modal-title">🌐 Route Specimen to Partner / Outsource Lab</h2>
                <p className="recv-modal-subtitle">
                  Select from approved reference laboratories mapped for this investigation.
                </p>
              </div>
              <button
                type="button"
                className="recv-modal-close"
                onClick={() => setIsOutsourceModalOpen(false)}
                disabled={submittingAssignLab}
              >
                ✕
              </button>
            </div>

            <div className="recv-modal-body">
              <div className="recv-outsource-test-card">
                <div className="recv-outsource-meta-row">
                  <span className="recv-outsource-test-name">🔬 {outsourceItem.itemName}</span>
                  <span className="recv-barcode-tag font-mono">🏷️ {outsourceItem.barcodeNo || 'No Barcode'}</span>
                </div>
                <div className="recv-outsource-sub-row">
                  <span>Patient: <strong>{outsourceItem.patientName}</strong> (UHID: {outsourceItem.patientId})</span>
                  <span>Registration: <strong>{outsourceItem.ledgerTransactionNo}</strong></span>
                </div>
              </div>

              {loadingLabs ? (
                <div style={{ textAlign: 'center', padding: '30px 0' }}>
                  <div className="recv-spinner" style={{ margin: '0 auto 12px' }}></div>
                  <p style={{ color: '#64748b' }}>Loading approved reference laboratories...</p>
                </div>
              ) : labOptions.length === 0 ? (
                <div className="recv-no-labs-box">
                  <span style={{ fontSize: '2rem' }}>⚠️</span>
                  <p style={{ fontWeight: 600, color: '#b45309', margin: '6px 0 2px' }}>No Active Reference Labs Mapped</p>
                  <p style={{ fontSize: '0.85rem', color: '#64748b' }}>
                    This investigation is not mapped to any partner laboratories yet. Please configure lab mappings in Masters → Reference Labs.
                  </p>
                </div>
              ) : (
                <div className="recv-form-group" style={{ marginTop: '16px' }}>
                  <label htmlFor="referenceLabSelect" style={{ fontWeight: 600, color: '#1e293b', display: 'block', marginBottom: '8px' }}>
                    Select Outsource Partner Lab <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    id="referenceLabSelect"
                    className="recv-select"
                    value={selectedLabId}
                    onChange={(e) => setSelectedLabId(e.target.value)}
                    style={{ fontSize: '0.95rem', padding: '10px 14px', borderRadius: '8px' }}
                  >
                    {labOptions.map((lab) => {
                      const labId = lab.referenceLabId ?? lab.referenceLabID;
                      return (
                        <option key={labId} value={labId}>
                          {lab.labName} ({lab.labCode}) — TAT: {lab.expectedTATHours || lab.defaultTATHours || 24}h {lab.accreditation ? `• ${lab.accreditation}` : ''}
                        </option>
                      );
                    })}
                  </select>

                  {/* Details of currently selected lab */}
                  {(() => {
                    const current = labOptions.find(
                      (l) => String(l.referenceLabId ?? l.referenceLabID) === String(selectedLabId)
                    );
                    if (!current) return null;
                    return (
                      <div className="recv-selected-lab-preview">
                        <div className="recv-slp-row">
                          <span><strong>Lab Code:</strong> {current.labCode}</span>
                          <span><strong>Expected TAT:</strong> ⏱️ {current.expectedTATHours || current.defaultTATHours || 24} hours</span>
                          {current.accreditation && <span><strong>Accreditation:</strong> 🏅 {current.accreditation}</span>}
                        </div>
                        <div className="recv-slp-row" style={{ marginTop: '6px', fontSize: '0.82rem', color: '#475569' }}>
                          <span><strong>External Code:</strong> {current.externalTestCode || 'N/A'}</span>
                          <span><strong>External Name:</strong> {current.externalTestName || current.itemName || 'Standard'}</span>
                          {current.contactPhone && <span><strong>Contact:</strong> 📞 {current.contactPhone}</span>}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="recv-modal-footer">
              <button
                type="button"
                className="recv-btn recv-btn-secondary"
                onClick={() => setIsOutsourceModalOpen(false)}
                disabled={submittingAssignLab}
              >
                Cancel
              </button>
              <button
                type="button"
                className="recv-btn recv-btn-primary"
                onClick={handleConfirmAssignOutsourceLab}
                disabled={submittingAssignLab || labOptions.length === 0 || !selectedLabId}
                style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', borderColor: '#7c3aed' }}
              >
                {submittingAssignLab ? 'Routing...' : '🚀 Route & Assign to Lab'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Contextual Bulk Action Bar */}
      {selectedTestIds.size > 0 && (
        <div className="lims-bulk-action-bar">
          <div className="lims-bulk-count">
            <span>☑ Selection:</span>
            <span className="lims-bulk-count-badge">
              {selectedTestIds.size} Selected ({selectedEligibleCount} Eligible)
            </span>
          </div>
          <button
            type="button"
            className="lims-bulk-btn-clear"
            onClick={handleClearSelection}
          >
            ✕ Clear Selection
          </button>
          <button
            type="button"
            className="lims-bulk-btn-receive"
            onClick={handleOpenBatchReceive}
            disabled={selectedEligibleCount === 0}
            title={
              selectedEligibleCount === 0
                ? 'No selected specimens are eligible for receiving'
                : `Receive all ${selectedEligibleCount} selected eligible specimen(s)`
            }
          >
            📥 Receive Selected ({selectedEligibleCount})
          </button>
        </div>
      )}

      {/* Patient & Order 360° Professional View-Only Modal */}
      <PatientOrder360Modal
        isOpen={Boolean(selectedPatientModal)}
        onClose={() => setSelectedPatientModal(null)}
        patientData={selectedPatientModal}
        onPrintBarcode={handleOpenPrintBarcode}
      />

      {/* Zero-Config Master-Driven Thermal Barcode Label Print Modal */}
      <ThermalBarcodePrintModal
        isOpen={isPrintModalOpen}
        onClose={() => {
          setIsPrintModalOpen(false);
          setPrintItem(null);
        }}
        sample={printItem}
        onPrintSuccess={() => {
          fetchSamples();
          setCountsRefreshKey((k) => k + 1);
        }}
      />
    </div>
  );
};

export default SampleReceivePage;
